import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";
import { TOSHI_LINK } from "../../utils/environmentVariables";
import { Typography, Button, styled } from "@mui/material";

import { GenericQuery } from "./__generated__/GenericQuery.graphql";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import SimpleBackdrop from "../../components/common/SimpleBackdrop";
import JsonListTable from "../../components/common/JsonListTable";

export type GenericParams = {
  id: string;
};

const Container = styled("div")(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  padding: 20,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
}));

const Generic: React.FC = () => {
  const { id } = useParams<GenericParams>();
  const validatedId: string = id ?? "";
  const data = useLazyLoadQuery<GenericQuery>(genericQuery, { id: validatedId });

  if (!data?.node) {
    return (
      <React.Suspense fallback={<SimpleBackdrop />}>
        <Container>
          <Typography variant="h5" gutterBottom>
            ID Not Found
          </Typography>
        </Container>
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={<SimpleBackdrop />}>
      <Container>
        <Typography variant="h5">
          {data?.node?.__typename}: {id}
        </Typography>
        <JsonListTable data={JSON.stringify(data?.node)} />
        <Button variant="contained" color="inherit" href={`${TOSHI_LINK}/Find/${id}`} target="_blank" rel="noreferrer">
          Toshi Link
        </Button>
      </Container>
    </React.Suspense>
  );
};

export const GenericPage: React.FC = () => {
  return (
    <ErrorBoundary>
      <React.Suspense fallback={<SimpleBackdrop />}>
        <Generic />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export const genericQuery = graphql`
  query GenericQuery($id: ID!) {
    node(id: $id) {
      __typename
      id
      ... on OpenquakeHazardTask {
        created
        duration
      }
      ... on OpenquakeHazardSolution {
        created
      }
      ... on File {
        id
        file_name
        file_size
        file_url
        md5_digest
      }
    }
  }
`;

export default GenericPage;
